import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Truck, Mail, Phone, MapPin, Home, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import { formatCOP } from '../store/cartStore';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderNumber}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (orderNumber) {
      fetchOrder();
      // Poll for payment status update
      const interval = setInterval(fetchOrder, 5000);
      return () => clearInterval(interval);
    }
  }, [orderNumber]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#B58A3F] animate-spin" />
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header onCartClick={() => {}} />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="heading-serif text-3xl text-[#142A1D] mb-4 font-light">
            Pedido no encontrado
          </h2>
          <button onClick={() => navigate('/')} className="btn-primary mt-6">
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="order-confirmation-page">
      <Header onCartClick={() => {}} />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-[#B58A3F]/10 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-12 h-12 text-[#B58A3F]" strokeWidth={1.5} />
          </motion.div>
          
          <p className="text-[#B58A3F] uppercase tracking-[0.3em] text-sm mb-3 font-medium">
            ¡Pedido Confirmado!
          </p>
          <h1 className="heading-serif text-4xl md:text-5xl text-[#142A1D] tracking-tight font-light mb-4">
            Gracias por tu compra
          </h1>
          <p className="text-[#142A1D] opacity-70 text-lg">
            Tu pedido ha sido recibido exitosamente. Nos pondremos en contacto contigo pronto.
          </p>
          <div className="inline-block mt-6 px-6 py-2 bg-[#F2EBE1] rounded-full border border-[#E5DEC5]">
            <span className="text-sm text-[#142A1D] opacity-60">Número de pedido:</span>{' '}
            <span className="font-medium text-[#142A1D]" data-testid="order-number">
              {order.order_number}
            </span>
          </div>
          
          {/* Payment Status Badge */}
          <div className="mt-4">
            {order.payment_status === 'paid' ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium border border-green-300">
                <CheckCircle className="w-4 h-4" strokeWidth={2} />
                Pago Aprobado
              </div>
            ) : order.payment_status === 'failed' || order.payment_status === 'cancelled' ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium border border-red-300">
                Pago no completado
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium border border-yellow-300">
                <Loader2 className="w-4 h-4 animate-spin" />
                Esperando confirmación de pago
              </div>
            )}
          </div>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl border border-[#E5DEC5] p-6 shadow-sm"
          >
            <h2 className="heading-serif text-2xl text-[#142A1D] mb-6 font-light flex items-center gap-3">
              <Package className="w-6 h-6 text-[#B58A3F]" strokeWidth={1.5} />
              Detalles del Pedido
            </h2>
            
            <div className="space-y-4 mb-6">
              {order.items.map((item, index) => (
                <div key={index} className="p-4 bg-[#FDFBF7] rounded-xl border border-[#E5DEC5]">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-[#142A1D]">Café Legatum</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-[#B58A3F]/10 text-[#B58A3F] rounded text-xs font-medium">
                          {item.profile}
                        </span>
                        <span className="inline-block px-2 py-0.5 bg-[#2B4C36]/10 text-[#2B4C36] rounded text-xs font-medium">
                          {item.grind}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-[#142A1D]">
                      x{item.quantity}
                    </span>
                  </div>
                  <p className="text-sm text-[#142A1D] opacity-60">
                    {formatCOP(item.unit_price)} c/u = {formatCOP(item.unit_price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="space-y-3 pt-4 border-t border-[#E5DEC5]">
              <div className="flex justify-between text-sm">
                <span className="text-[#142A1D] opacity-70">Subtotal</span>
                <span className="text-[#142A1D] font-medium">{formatCOP(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#142A1D] opacity-70 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />
                  Envío
                </span>
                <span className="text-[#142A1D] font-medium">{formatCOP(order.shipping_cost)}</span>
              </div>
              <div className="pt-3 border-t border-[#E5DEC5] flex justify-between items-baseline">
                <span className="text-lg font-medium text-[#142A1D]">Total</span>
                <span className="text-3xl font-light text-[#B58A3F] heading-serif">
                  {formatCOP(order.total)}
                </span>
              </div>
            </div>
          </motion.div>
          
          {/* Customer Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white rounded-2xl border border-[#E5DEC5] p-6 shadow-sm"
          >
            <h2 className="heading-serif text-2xl text-[#142A1D] mb-6 font-light flex items-center gap-3">
              <Truck className="w-6 h-6 text-[#B58A3F]" strokeWidth={1.5} />
              Información de Envío
            </h2>
            
            <div className="space-y-4">
              <InfoRow icon={User} label="Nombre" value={order.customer.name} />
              <InfoRow icon={Phone} label="Teléfono" value={order.customer.phone} />
              <InfoRow icon={Mail} label="Correo" value={order.customer.email} />
              <InfoRow icon={Home} label="Dirección" value={order.customer.address} />
              <InfoRow icon={MapPin} label="Ciudad" value={order.customer.city} />
              {order.customer.notes && (
                <div className="pt-3 border-t border-[#E5DEC5]">
                  <p className="text-xs text-[#142A1D] opacity-60 uppercase tracking-wider mb-1">
                    Notas
                  </p>
                  <p className="text-sm text-[#142A1D]">{order.customer.notes}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
        
        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 bg-gradient-to-br from-[#142A1D] to-[#2B4C36] rounded-2xl p-8 text-[#FDFBF7]"
        >
          <h3 className="heading-serif text-2xl font-light mb-4">Próximos pasos</h3>
          <div className="space-y-3 text-[#F2EBE1] opacity-90">
            <p className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#B58A3F] rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">1</span>
              <span>Recibirás una confirmación por correo con los detalles de tu pedido.</span>
            </p>
            <p className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#B58A3F] rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">2</span>
              <span>Nos comunicaremos contigo al teléfono {order.customer.phone} para coordinar el pago.</span>
            </p>
            <p className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#B58A3F] rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 mt-0.5">3</span>
              <span>Prepararemos tu café {order.items[0]?.grind === 'Molido' ? 'molido fresco' : 'en grano'} y lo enviaremos a tu dirección.</span>
            </p>
          </div>
        </motion.div>
        
        <div className="text-center mt-8">
          <motion.button
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Volver a la Tienda
          </motion.button>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-[#B58A3F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-[#142A1D] opacity-60 uppercase tracking-wider">
        {label}
      </p>
      <p className="text-sm text-[#142A1D] font-medium break-words">{value}</p>
    </div>
  </div>
);

export default OrderConfirmationPage;
